import { ChangeDetectorRef, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { noop, Observable, Subject } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { InviteUserComponent } from 'app/modules/settings/modals/invite-user/invite-user.component';
import { AddTeamComponent } from 'app/modules/settings/modals/add-team/add-team.component';
import { AccountService } from 'app/core';
import { Account, Team } from 'app/core/models/account.types';
import { Invitation, User, UserAssociation } from 'app/core/models/user.types';
import { UserService } from 'app/layout/common/user/user.service';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { ActivatedRoute, Data } from '@angular/router';
import { Role } from 'app/core/models/role';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { UserAssociationService } from 'app/core/services/user-association.service';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { environment } from '@env/environment';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';
import { Dialog } from '@angular/cdk/dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

type RouteData = Data & { roles: Role[] };

@Component({
  selector: 'user-settings',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserSettingsComponent extends OnDestroyMixin implements OnInit {
  private _unsubscribeAll: Subject<void>;
  public account: Account;
  public users: User[];
  public userAssociations: UserAssociation[];
  userAssociationsDataSource: MatTableDataSource<any>;
  public usersTableColumns: string[];
  public invitations: Invitation[];
  public invitations$: Observable<Invitation[]>;
  public invitationsTableColumns: string[];
  public userRoles: Role[];
  public isCurrentUserAdmin: boolean = false;
  public currentUser: User;
  public teams: Team[];

  newPermissionFeatureEnabled = inject(Store).selectSnapshot(
    AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
  );

  constructor(
    private _accountService: AccountService,
    private _userService: UserService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _matDialog: MatDialog,
    private dialog: Dialog,
    private readonly userAssociationService: UserAssociationService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly confirmService: ConfirmService,
    public snackBar: MatSnackBar,
  ) {
    super();
    this._unsubscribeAll = new Subject();
    this.usersTableColumns = ['id', 'full_name', 'email', 'role'];
    if (!this.newPermissionFeatureEnabled) {
      this.usersTableColumns = [...this.usersTableColumns, 'actions'];
    } else {
      this.usersTableColumns = [...this.usersTableColumns, 'primary_team_id', 'actions'];
    }
    this.invitationsTableColumns = ['email', 'full_name', 'created_at', 'actions'];
    this.userAssociationsDataSource = new MatTableDataSource();
  }

  ngOnInit(): void {
    this._accountService.currentAccount.subscribe((accountData) => {
      this.account = accountData;
    });
    // Get the users
    this._accountService.userAssociations$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((userAssociations: UserAssociation[]) => {
        if (userAssociations) {
          this.userAssociations = userAssociations.filter((x) => x.user);
          this.userAssociations.sort((a, b) => Number(b?.user.active) - Number(a?.user.active));
          this.userAssociationsDataSource = new MatTableDataSource();
          this.userAssociationsDataSource.data = this.userAssociations;
          this.checkIfAdmin();
          console.log(this.userAssociations);
        }

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    // Get the invitations
    this.invitations$ = this._accountService.invitations$;
    this._accountService.invitations$.pipe(takeUntil(this._unsubscribeAll)).subscribe((invitations: Invitation[]) => {
      this.invitations = invitations;

      // Mark for check
      this._changeDetectorRef.markForCheck();
    });

    this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
      if (user) {
        this.currentUser = user;
        this.checkIfAdmin();
      }
      this._changeDetectorRef.markForCheck();
    });

    this._accountService.teams$.pipe(takeUntil(this._unsubscribeAll)).subscribe((teams: Team[]) => {
      this.teams = teams;
      this._changeDetectorRef.markForCheck();
    });

    this.activatedRoute.data.subscribe(({ roles }: RouteData) => (this.userRoles = roles));
  }

  deleteInvitation(invitation): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this._accountService.deleteInvitation(invitation.id).subscribe();
      });
  }

  checkIfAdmin(): void {
    if (this.currentUser && this.userAssociations) {
      let foundUserAssociations = this.userAssociations.filter((ua) => {
        return ua.user.id == this.currentUser.id;
      });
      if (foundUserAssociations && foundUserAssociations.length > 0) {
        this.isCurrentUserAdmin = true;
      }
    }
  }

  inviteUser(): void {
    const dialogRef = this.dialog.open(InviteUserComponent, {
      panelClass: 'mailbox-compose-dialog',
    });

    dialogRef.closed
      .pipe(
        tap(() => this._accountService.bustInvitationsCacheBuster()),
        switchMap(() => this._accountService.getInvitations()),
      )
      .subscribe(noop);
  }

  addTeam() {
    this._matDialog.open(AddTeamComponent, {
      panelClass: 'mailbox-compose-dialog',
    });
  }

  onChangeUserAtt(
    { value }: MatSelectChange,
    userAssociation: UserAssociation,
    property: 'role_id' | 'primary_team_id' = 'role_id',
  ): void {
    userAssociation[property] = value;
    this.userAssociationService.update(userAssociation).pipe(untilComponentDestroyed(this)).subscribe(noop);
  }

  toggleUserTeam(team, user, change: MatCheckboxChange) {
    if (change.checked) {
      this._accountService.addUserToTeam(team, user).subscribe();
    } else {
      this._accountService.removeUserFromTeam(team, user).subscribe();
    }
  }

  teamIncludesUser(team, user) {
    return team.users.map((u) => u.id).includes(user.id);
  }

  deleteTeam(team) {
    this.confirmService
      .openConfirmationModal({ message: 'Are you sure you want to delete this users?' })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this._accountService.deleteTeam(team.id).subscribe();
      });
  }

  disableUser(userAssociation) {
    this.confirmService
      .openConfirmationModal({ message: 'Are you sure that you wish to disable this user?' })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        //[sc-46758] - do not make user inactive, just remove association to account
        this._accountService.deleteUserAssociation(userAssociation).subscribe();
        //[sc-9647]: do not remove for now, just disable
        //this._accountService.toggleActiveUserAssociation(userAssociation).subscribe();
      });
  }

  restoreUser(userAssociation) {
    this.confirmService
      .openConfirmationModal({
        title: 'Restore Confirmation',
        message: 'Are you sure that you wish to restore this user?',
      })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this._accountService.toggleActiveUserAssociation(userAssociation).subscribe();
      });
  }

  getEmailInvitationLink(invitation: Invitation): string {
    const subject = 'Your Invitation to TermSheet';
    const link = `https://dashboard.${environment.appDomain}/sign-up?invite_token=${invitation.token}`;
    const body = `Please use the following link to sign up for TermSheet. ${link}`;
    return `mailto:${invitation.email}?subject=${subject}&body=${body}`;
  }

  resendInvitation(invitation: Invitation): void {
    this._accountService
      .resendInvitation(invitation.id)
      .subscribe(() => this.snackBar.open('Invitation has been resent.', 'OK'));
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
